import React, { useState } from 'react';
import styled, { css } from 'styled-components/macro';
import { IfUserHasPermission } from '../../../authorisation/IfUserHasPermission';
import { copyTextToClipboard } from '../../../helpers/copyToClipboard';
import { useTemporaryBoolean } from '../../../helpers/hooks/useTemporaryBoolean';
import { ReactComponent as ApartmentIcon } from '../../../images/icons/apartment.svg';
import { ReactComponent as CopyIcon } from '../../../images/icons/copy.svg';
import { ReactComponent as PencilIcon } from '../../../images/icons/pencil.svg';
import { ReactComponent as PersonIcon } from '../../../images/icons/person.svg';
import { Badge } from '../../../shared/Badge';
import { ButtonIconLeft, LinkButton } from '../../../shared/Button';
import { FadingMessage } from '../../../shared/FadingMessage';
import { ExternalLink } from '../../../shared/Link';
import {
  colourNeutral02,
  colourNeutral10,
  colourNeutral11,
  colourPurple04,
  colourPurple09,
} from '../../../styling/colours';
import { fontSize01, fontSize03, fontSize04, fontWeightBold } from '../../../styling/fonts';
import { spacing01, spacing03, spacing04, spacing05, spacing07 } from '../../../styling/spacing';
import { BuildTypeCode, buildTypeDisplayNames } from '../project';
import { EditProjectModal } from '../project-edit/EditProjectModal';

export const projectHeaderTestId = 'project-header';

type Props = {
  project: Project;
};

type ProjectCustomer = {
  customerId: number;
  customerReference: string;
  name: string;
};

export type Project = {
  projectId: number;
  siteAddress: string;
  projectReference: string;
  customer: ProjectCustomer;
  buildTypeCode: BuildTypeCode;
  projectFolderLink: string;
  alternativeCustomers: Array<ProjectCustomer>;
  externalAirTemperatureInDegreesCelsius: number;
};

export const ProjectHeader = ({ project }: Props) => {
  const [shouldShowCopiedMessage, showCopiedMessage] = useTemporaryBoolean();

  const copyProjectFolderLinkToClipboard = () => {
    copyTextToClipboard(project.projectFolderLink);
    showCopiedMessage();
  };

  const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);

  return (
    <>
      <EditProjectModal isOpen={isEditProjectModalOpen} closeModal={() => setIsEditProjectModalOpen(false)} />
      <Container data-testid={projectHeaderTestId}>
        <Row>
          <Column>
            <ProjectReferenceContainer>
              <ProjectReferenceNumber>{project.projectReference}</ProjectReferenceNumber>
              {project.buildTypeCode === 'Retrofit' ? (
                <BuildTypeBadge
                  $backgroundColor={colourNeutral02}
                  $textColour={colourNeutral10}
                  $borderColour="transparent"
                >
                  {buildTypeDisplayNames[project.buildTypeCode]}
                </BuildTypeBadge>
              ) : (
                <BuildTypeBadge
                  $backgroundColor={colourNeutral10}
                  $textColour={colourNeutral02}
                  $borderColour="transparent"
                >
                  {buildTypeDisplayNames[project.buildTypeCode]}
                </BuildTypeBadge>
              )}
            </ProjectReferenceContainer>
            <ProjectFolderLink href={project.projectFolderLink}>{project.projectFolderLink}</ProjectFolderLink>
            {document.queryCommandSupported('copy') && (
              <CopyButton title="Copy to clipboard" onClick={copyProjectFolderLinkToClipboard} fill={colourNeutral11} />
            )}
            <CopiedMessage $visible={shouldShowCopiedMessage}>Copied</CopiedMessage>
          </Column>
          <DetailsColumn>
            <ProjectDetailsText>
              <AddressIcon fill={colourNeutral11} />
              {project.siteAddress}
            </ProjectDetailsText>
          </DetailsColumn>
          <IfUserHasPermission requiredPermission="Edit">
            <EditButton onClick={() => setIsEditProjectModalOpen(true)}>
              <ButtonIconLeft>
                <PencilIcon />
              </ButtonIconLeft>
              Edit Project
            </EditButton>
          </IfUserHasPermission>
        </Row>
      </Container>
    </>
  );
};

const Container = styled.div`
  background-color: ${colourPurple04};
  color: ${colourNeutral11};
  padding: ${spacing05} ${spacing05};
`;

const Row = styled.div`
  display: flex;
`;

const Column = styled.div`
  padding-right: ${spacing07};
  flex: 1;
`;

const DetailsColumn = styled(Column)`
  flex: 1;
`;

const ProjectDetailsText = styled.p`
  margin: 0;
  font-size: ${fontSize03};

  :not(:first-of-type) {
    margin-top: ${spacing03};
  }
`;

const ProjectReferenceContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing04};
`;

const ProjectReferenceNumber = styled.span`
  font-size: ${fontSize04};
  letter-spacing: 3px;
  font-weight: ${fontWeightBold};
`;

const BuildTypeBadge = styled(Badge)`
  margin-left: ${spacing04};
  letter-spacing: 0.75px;
  text-transform: uppercase;
  font-size: ${fontSize01};
`;

const ProjectFolderLink = styled(ExternalLink)`
  font-size: ${fontSize03};
  color: ${colourNeutral11};
`;

const iconAlignedWithText = css`
  padding: 0 ${spacing01} 0 0;
  height: 1em;
  width: 1em;
  top: 0.125em;
  position: relative;
  margin-right: ${spacing01};
`;

const CustomerIcon = styled(PersonIcon)`
  ${iconAlignedWithText}
`;

const AddressIcon = styled(ApartmentIcon)`
  ${iconAlignedWithText}
`;

const CopyButton = styled(CopyIcon)`
  ${iconAlignedWithText};
  cursor: pointer;
  transition: all 0.25s ease;
  margin-left: ${spacing03};

  &:hover {
    opacity: 0.7;
  }
`;

const CopiedMessage = styled(FadingMessage)`
  color: ${colourPurple09};
`;

const EditButton = styled(LinkButton)`
  color: white;
`;
